import { Injectable } from '@angular/core';
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { ILanguageCodeValue } from "libraries/pipes/localized/localized.interface";
import { forkJoin, Observable, of } from "rxjs";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import { map } from "rxjs/operators";
import { Yii } from "libraries/interfaces/Yii";
import {
    faBed, faChartLine,
    faCogs,
    faFolderOpen,
    faHome,
    faNewspaper,
    faPlane, faQuestion
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/free-regular-svg-icons";

import { IWenji } from "libraries/interfaces/IWenji";
import { IDashboardConfig } from "libraries/interfaces/i-dashboard-config";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";

declare const wenji: IWenji;

export interface IAppCard {
    [key: string]: IAppCardLink[];
}

export interface IAppCardLink {
    code?: string;
    permission?: string;
    permissionStart?: string;
    text: string;
    url: string;
    icon: IconDefinition;
}

export interface IHomeConfig {
    globalName: string;
    applicationArray: IHomeApplication[];
}

export interface IHomeApplication {
    name: string;
    descriptionArray: ILanguageCodeValue[];
    code: string;
    icon: string;
}

export const CONFIG_HOME: IHomeConfig = {
    globalName: "Travel Suite",
    applicationArray: [
        {
            code: 'sasha',
            icon: 'fa-search',
            name: 'Sasha',
            descriptionArray: [
                {
                    languageCode: 'FR',
                    value: 'Gestion de dossiers multi-source'
                },
                {
                    languageCode: "EN",
                    value: "Multi-source booking management"
                }
            ]
        },
        {
            code: 'iaqobo',
            name: 'Iaqobo',
            icon: 'fa-binoculars',
            descriptionArray: [
                {
                    languageCode: 'FR',
                    value: 'Moteur de réservation'
                },
                {
                    languageCode: "EN",
                    value: "Booking engine"
                }
            ]
        },
        {
            code: 'dashboard',
            icon: 'fa-chart-line',
            name: 'Dashboard',
            descriptionArray: [
                {
                    languageCode: 'FR',
                    value: 'Tableau de bord'
                },
                {
                    languageCode: "EN",
                    value: "Dashboard"
                }
            ]
        },
    ]
};

@Injectable()
export class HomeDomainService {

    private appCardSource: IAppCard = {
        sasha: [
            {
                text: Yii.t('wui-shared-js', 'Accueil'),
                url: '../sasha/home',
                icon: faHome,
                permissionStart: 'sasha.use',
                code: 'sashaHome',
            },
            {
                text: Yii.t('wui-shared-js', 'Dossiers'),
                url: '../sasha/booking',
                icon: faFolderOpen,
                permissionStart: 'sasha.use',
                code: 'sashaBooking',

            },
            {
                text: Yii.t('wui-shared-js', 'Traitements'),
                url: '../sasha/processing/search',
                icon: faCogs,
                permissionStart: 'sasha.processing',
                code: 'sashaProcessing',
            },
            {
                text: Yii.t('wui-shared-js', "Journal d'émissions"),
                url: '../sasha/ticketing-report',
                icon: faNewspaper,
                permissionStart: 'sasha.ticketingReport',
                code: 'sashaTicketingReport',

            },
            {
                text: Yii.t('wui-shared-js', "Journal des incidents"),
                url: '../sasha/process-report',
                icon: faQuestion,
                permissionStart: 'sasha.processReport',
                code: 'sashaProcessReport',

            },
        ],
        iaqobo: [
            {
                text: Yii.t('wui-shared-js', 'Air'),
                url: '../locobook/search',
                icon: faPlane,
                permissionStart: 'iqba.use',
                code: 'iqbAir',

            },
            {
                text: Yii.t('wui-shared-js', 'Hôtel'),
                url: '../iaqobohotel/search',
                icon: faBed,
                permissionStart: 'iqbh.use',
                code: 'iqbHotel',

            },
        ],
        dashboard: [
            {
                text: Yii.t('wui-shared-js', 'Accueil'),
                url: '../dashboard/home',
                icon: faHome,
                permissionStart: 'dashboard.use',
                code: 'dashboardHome',
            },
        ],
    };

    private appCardCache: IAppCard;

    constructor(
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly permissionService: PermissionResourceService,
        private readonly localizedPipe: LocalizedPipe,
    ) {}

    public getConfig(): Observable<IHomeConfig> {
        return forkJoin([
            this.configurationResourceService.get(['wui', 'config', 'home', 'home.json'], CONFIG_HOME),
            this.configurationResourceService.get(['wui', 'app_dashboard', 'config','json', 'dashboard-config.json'], null),
            this.permissionService.getPermissions(),
        ])
            .pipe(
                map(([homeConfig, dashboardConfig, permissions]: [IHomeConfig, IDashboardConfig[], Set<string>]): IHomeConfig => {
                    const outputConfig: IHomeConfig = {
                        globalName: homeConfig.globalName,
                        applicationArray: [],
                    };
                    if (permissions.has('sasha.use')) {
                        const sashaApplication: IHomeApplication = homeConfig.applicationArray
                            .find((appli: IHomeApplication): boolean => appli.code === 'sasha');
                        outputConfig.applicationArray.push(sashaApplication);
                    }

                    const hasIqbaPermission: boolean = permissions.has(('iqba.use'));
                    const hasIqbhPermission: boolean = permissions.has(('iqbh.use'));
                    if (hasIqbaPermission || hasIqbhPermission) {
                        const iaqoboApplication: IHomeApplication = homeConfig.applicationArray
                            .find((appli: IHomeApplication): boolean => appli.code === 'iaqobo');
                        outputConfig.applicationArray.push(iaqoboApplication);
                    }
                    if (permissions.has('dashboard.use')) {
                        const dashboardApplication: IHomeApplication = homeConfig.applicationArray
                            .find((appli: IHomeApplication): boolean => appli.code === 'dashboard');
                            if (dashboardApplication) {
                                outputConfig.applicationArray.push(dashboardApplication);
                            }
                    }
                    if (this.appCardSource.dashboard && dashboardConfig && homeConfig.applicationArray.find((app: IHomeApplication): boolean => app.code === "dashboard")) {
                        dashboardConfig.forEach((row: IDashboardConfig, index: number): void => {
                            this.appCardSource.dashboard.push({
                                text: row.nameArray?.length ?
                                    this.localizedPipe.transform(row.nameArray) :
                                    row.name,
                                url: `../dashboard/${index}`,
                                icon: faChartLine,
                                permissionStart: 'dashboard.use',
                                code: 'dashboardHome',
                            });
                        });
                    }

                    return outputConfig;
                })
            );
    }

    public getAppCard(): Observable<IAppCard> {
        if (this.appCardCache) {
            return of(this.appCardCache);
        }
        return this.permissionService.getPermissions()
            .pipe(
                map(
                    (permissions: Set<string>): IAppCard => {
                        this.appCardCache = {};
                        if (permissions.has('sasha.use')) {
                            const sasha: IAppCardLink[] = this.extractLinks(this.appCardSource.sasha, permissions);
                            this.appCardCache = {
                                ...this.appCardCache,
                                sasha
                            };
                        }
                        if (permissions.has('iqba.use') || permissions.has('iqbh.use')) {
                            const iaqobo: IAppCardLink[] = this.extractLinks(this.appCardSource.iaqobo, permissions);

                            this.appCardCache = {
                                ...this.appCardCache,
                                iaqobo,

                            };
                        }
                        if (permissions.has('dashboard.use')) {
                            const dashboard: IAppCardLink[] = this.extractLinks(this.appCardSource.dashboard, permissions);
                            this.appCardCache = {
                                ...this.appCardCache,
                                dashboard
                            };
                        }
                        return this.appCardCache;

                    }
                )
            );
    }

    private extractLinks(links: IAppCardLink[], permissions: Set<string>): IAppCardLink[] {
        return links.filter((link: IAppCardLink): boolean => {
            return Array.from(permissions)
                .findIndex((permission: string): boolean => permission.includes(link.permissionStart)) > -1;
        });
    }
}
